import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { addFleet } from "../redux/weeklyRefreshSlice"
import getFleetExecutionTask from '../services/getFleetExecutionTask'

// Combines depvar tasks from split machine names into logical depvar units
function combineDepvarTasks(tasks: any[]) {
  // Filter tasks related to depvar processing
  const depvarTasks = tasks.filter((t: any) => 
    t.machine_name === "standard_fleet" || 
    t.machine_name === "recast_model_run_only" || 
    t.machine_name === "recast_post_model_run_analysis"
  )

  // Group tasks by depvar name
  const depvarGroups: { [key: string]: any[] } = {}
  
  depvarTasks.forEach((task: any) => {
    const depvar = task.machine_input?.depvar
    if (depvar) {
      if (!depvarGroups[depvar]) {
        depvarGroups[depvar] = []
      }
      depvarGroups[depvar].push(task)
    }
  })

  // Combine tasks within each depvar group
  return Object.values(depvarGroups).map((group: any[]) => {
    // Prefer Airflow machine names over Batch (migration-friendly)
    const preferredTask = group.find(t => t.machine_name === "recast_model_run_only") ||
                         group.find(t => t.machine_name === "recast_post_model_run_analysis") ||
                         group.find(t => t.machine_name === "standard_fleet") ||
                         group[0]

    // Determine combined status: Failed > Running > Succeeded (only if ALL succeeded) > Unknown
    let combinedStatus = "Unknown"
    let hasFailure = false
    let hasRunning = false
    let allSucceeded = group.length > 0 // Start true only if we have tasks
    
    for (const task of group) {
      if (task.status === "Failed") {
        hasFailure = true
        break // Stop checking once we find failure
      } else if (task.status === "Running") {
        hasRunning = true
        allSucceeded = false
      } else if (task.status !== "Succeeded" && task.status !== "Completed") {
        allSucceeded = false // Any non-success status breaks the chain
      }
    }
    
    // Apply status priority
    if (hasFailure) {
      combinedStatus = "Failed"
    } else if (hasRunning) {
      combinedStatus = "Running"
    } else if (allSucceeded) {
      combinedStatus = "Succeeded"
    } else {
      combinedStatus = "Unknown"
    }

    // Get earliest start and latest end dates
    const startDates = group.map(t => t.start_date).filter(d => d)
    const endDates = group.map(t => t.end_date).filter(d => d)
    
    const earliestStart = startDates.length > 0 ? 
      new Date(Math.min(...startDates.map(d => new Date(d).getTime()))).toISOString() : 
      preferredTask.start_date

    const latestEnd = endDates.length > 0 ? 
      new Date(Math.max(...endDates.map(d => new Date(d).getTime()))).toISOString() : 
      preferredTask.end_date

    return {
      ...preferredTask,
      status: combinedStatus,
      start_date: earliestStart,
      end_date: latestEnd
    }
  })
}

export default function useWeeklyRefreshFleet(fleet_execution_id: string){
  
  const [fleetState, setFleetState] = useState<any>(null)
  const [errors, setErrors] = useState<boolean|null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [viewFleet, setViewFleet] = useState(false)

  let weeklyRefresh: any = useSelector((state: any) => state.weeklyRefresh)
  
  const dispatch = useDispatch()

  const found:any = weeklyRefresh?.fleets[fleet_execution_id]

  useEffect(() => {
    setLoading(true)    
    const min_age = Date.now() - 30 * 1000 // 30 seconds
    if (!found || found.time_added < min_age) {
      getFleetExecutionTask(fleet_execution_id)
        .then(response => {
          if(response.status === 200){  
            const tasks = response.data.execution_tasks
            const fleet_data = {[fleet_execution_id]: {
              depvars: combineDepvarTasks(tasks),
              ingest: tasks.filter((t:any) => 
                t.machine_name === "recast_data_ingest" || t.machine_name === "recast_full_model_run" || t.machine_name === "multi_depvar"
              ),
              deploy: tasks.filter((t:any) => 
                t.machine_name === "recast_scheduled_deploy" || t.machine_name === "standard_deploy"
              ),
              time_added: Date.now()
            }};
            dispatch(addFleet(fleet_data));
            setFleetState(fleet_data)
          }
        })
        .catch((error) => {
          setErrors(true)
        })
        .finally(() => setLoading(false))
    } else {
      setFleetState(found)
      setLoading(false)
    }
  },[fleet_execution_id, dispatch, found])

  const handleView = () => {
    setViewFleet(!viewFleet)
  }

  return{fleetState, errors, loading, viewFleet, handleView}
}
